// Simple, distinct line-diagram icons — one per Sales & Marketing Program course.
// Each is a small, literal depiction of the course's core idea, not a generic bookmark glyph.
import type { ReactElement } from "react";

const S = { stroke: "currentColor", fill: "none", strokeWidth: 2.6, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };

const ICONS: Record<string, ReactElement> = {
  // MKT101 — Marketing Foundations: a positioning bullseye
  "mkt101-marketing-foundations": (
    <g {...S}>
      <circle cx="24" cy="24" r="17" />
      <circle cx="24" cy="24" r="10" />
      <circle cx="24" cy="24" r="2.6" fill="currentColor" stroke="none" />
    </g>
  ),
  // SAL101 — Selling Foundations: two parties reaching agreement
  "sal101-selling-foundations": (
    <g {...S}>
      <circle cx="17" cy="24" r="12" />
      <circle cx="31" cy="24" r="12" />
      <path d="M20 24l3.2 3.5L29 20" />
    </g>
  ),
  // BUS101 — Professional Communication: a structured message
  "bus101-professional-communication": (
    <g {...S}>
      <path d="M8 12h32a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H21l-8 7v-7h-2a3 3 0 0 1-3-3V15a3 3 0 0 1 3-3z" />
      <path d="M14 20h20M14 26h13" />
    </g>
  ),
  // MKT102 — Consumer & Buyer Psychology: a mind with a decision gear
  "mkt102-consumer-buyer-psychology": (
    <g {...S}>
      <path d="M17 30v-3a10 10 0 1 1 14 0v3a3 3 0 0 1-3 3h-8a3 3 0 0 1-3-3z" />
      <path d="M19 33v3M29 33v3" />
      <circle cx="34" cy="12" r="5.5" />
      <path d="M34 5v2M34 17v2M27.5 12h2M38.5 12h2M29.7 7.2l1.4 1.4M36.9 15.4l1.4 1.4M29.7 16.8l1.4-1.4M36.9 8.6l1.4-1.4" strokeWidth="1.8" />
    </g>
  ),
  // SAL102 — Lead Generation: prospects funneling into a pipeline
  "sal102-lead-generation": (
    <g {...S}>
      <circle cx="14" cy="8" r="2" fill="currentColor" stroke="none" />
      <circle cx="24" cy="6" r="2" fill="currentColor" stroke="none" />
      <circle cx="34" cy="8" r="2" fill="currentColor" stroke="none" />
      <path d="M9 13h30l-9 14v10l-12 5V27z" />
    </g>
  ),
  // MKT103 — Market Research: magnifier over evidence bars
  "mkt103-market-research": (
    <g {...S}>
      <rect x="8" y="26" width="6" height="12" />
      <rect x="17" y="20" width="6" height="18" />
      <rect x="26" y="14" width="6" height="24" />
      <circle cx="34" cy="16" r="7" />
      <path d="M39 21l4 4" />
    </g>
  ),
  // MKT201 — Segmentation, Targeting & Positioning: one segment chosen from several
  "mkt201-segmentation-positioning": (
    <g {...S}>
      <circle cx="12" cy="24" r="4.5" fill="currentColor" stroke="none" />
      <circle cx="24" cy="24" r="4.5" fill="currentColor" stroke="none" />
      <circle cx="36" cy="24" r="4.5" fill="currentColor" stroke="none" />
      <circle cx="24" cy="24" r="13" strokeDasharray="3 4" />
    </g>
  ),
  // SAL201 — Consultative Selling: discovery through questions
  "sal201-consultative-selling": (
    <g {...S}>
      <path d="M6 12h24a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H16l-6 5v-5H6a3 3 0 0 1-3-3V15a3 3 0 0 1 3-3z" />
      <text x="18" y="26" fontSize="15" fontWeight="700" stroke="none" fill="currentColor" textAnchor="middle">?</text>
      <circle cx="38" cy="34" r="7" />
      <path d="M35.3 34.3l2 2 3.4-4" strokeWidth="2.2" />
    </g>
  ),
  // MKT202 — Brand Strategy: a durable identity shield
  "mkt202-brand-strategy": (
    <g {...S}>
      <path d="M24 6l15 5v11c0 10-6.5 16.5-15 21-8.5-4.5-15-11-15-21V11z" />
      <path d="M17 24l5 5 9-10" />
    </g>
  ),
  // SAL202 — Complex B2B Sales: multiple stakeholders, one deal
  "sal202-complex-b2b-sales": (
    <g {...S}>
      <circle cx="24" cy="10" r="5" />
      <circle cx="10" cy="34" r="5" />
      <circle cx="38" cy="34" r="5" />
      <path d="M24 15l-11 15M24 15l11 15M15 34h18" />
    </g>
  ),
  // MKT203 — Digital Acquisition: a channel broadcasting reach
  "mkt203-digital-acquisition": (
    <g {...S}>
      <rect x="7" y="12" width="26" height="18" rx="2" />
      <path d="M15 36h10M20 30v6" />
      <path d="M36 16a10 10 0 0 1 0 12M40 12a16 16 0 0 1 0 20" strokeWidth="2.2" />
    </g>
  ),
  // BUS201 — Negotiation: a balanced deal
  "bus201-negotiation": (
    <g {...S}>
      <path d="M24 8v28M15 12h18" />
      <path d="M7 16l8-4 8 4-4 10a5 5 0 0 1-8 0z" />
      <path d="M25 16l8-4 8 4-4 10a5 5 0 0 1-8 0z" />
      <path d="M17 40h14" />
    </g>
  ),
  // MKT204 — Customer Retention: a loop that keeps customers close
  "mkt204-customer-retention": (
    <g {...S}>
      <path d="M12 16a14 14 0 0 1 24-3M36 32a14 14 0 0 1-24 3" />
      <path d="M31 8l5 5-5 5M17 40l-5-5 5-5" />
      <path d="M24 20l3 3-3 7-3-7z" fill="currentColor" stroke="none" />
    </g>
  ),
  // MKT301 — Marketing Analytics: a real dashboard, not vanity metrics
  "mkt301-marketing-analytics": (
    <g {...S}>
      <path d="M6 38V10M6 38h36" />
      <rect x="12" y="26" width="5" height="12" fill="currentColor" stroke="none" />
      <rect x="21" y="18" width="5" height="20" fill="currentColor" stroke="none" />
      <rect x="30" y="22" width="5" height="16" fill="currentColor" stroke="none" />
      <path d="M11 20l8-7 7 4 9-9" />
    </g>
  ),
  // SAL301 — Sales Management: a team structure with real ownership
  "sal301-sales-management": (
    <g {...S}>
      <circle cx="24" cy="9" r="5" />
      <circle cx="11" cy="34" r="5" />
      <circle cx="24" cy="34" r="5" />
      <circle cx="37" cy="34" r="5" />
      <path d="M24 14v10M24 24l-13 5M24 24l13 5" />
    </g>
  ),
  // MKT302 — Growth Strategy: deliberate, staged growth
  "mkt302-growth-strategy": (
    <g {...S}>
      <path d="M6 38h6v-6h8v-8h8v-8h8v-8h4" />
      <path d="M32 8h8v8" />
    </g>
  ),
  // MKT303 — Marketing & Sales Economics: real unit economics
  "mkt303-marketing-sales-economics": (
    <g {...S}>
      <circle cx="24" cy="19" r="12" />
      <text x="24" y="24" fontSize="15" fontWeight="700" stroke="none" fill="currentColor" textAnchor="middle">$</text>
      <path d="M8 40h32" />
      <path d="M14 40v-6M24 40v-9M34 40v-4" strokeWidth="2.2" />
    </g>
  ),
  // MKT/SAL401 — Integrated Go-To-Market Strategy: every prior chapter, one direction
  "mkt-sal401-gtm-strategy": (
    <g {...S}>
      <circle cx="24" cy="24" r="17" />
      <path d="M24 24l7-11 -3 12z" fill="currentColor" stroke="currentColor" strokeLinejoin="round" />
      <circle cx="24" cy="24" r="2.4" fill="currentColor" stroke="none" />
    </g>
  ),
  // MKT402 — AI-Driven Marketing & Sales: a governed, connected system
  "mkt402-ai-driven-marketing": (
    <g {...S}>
      <circle cx="24" cy="12" r="4.5" />
      <circle cx="10" cy="34" r="4.5" />
      <circle cx="38" cy="34" r="4.5" />
      <circle cx="24" cy="24" r="4.5" />
      <path d="M24 16.5V19.5M20.9 26.6l-8 5.5M27.1 26.6l8 5.5" />
    </g>
  ),
  // CAP400 — Business Growth Blueprint (capstone): the defended result
  "cap400-business-growth-blueprint": (
    <g {...S}>
      <path d="M14 8h20v9c0 7-4.5 12-10 14-5.5-2-10-7-10-14z" />
      <path d="M18 31l-3 9 9-4 9 4-3-9" />
    </g>
  ),
};

export function hasCourseCoverIcon(courseId: string): boolean {
  return courseId in ICONS;
}

export function CourseCoverIcon({ courseId }: { courseId: string }) {
  const icon = ICONS[courseId];
  if (!icon) return null;
  return (
    <svg viewBox="0 0 48 48" width="100%" height="100%" aria-hidden="true">
      {icon}
    </svg>
  );
}
